import { format } from 'date-fns';
import { useState, useEffect, useContext } from 'react';
import { useOutletContext, useParams } from "react-router-dom";
import Protect from '../components/Layout/Protect';
import Button from '../components/UI/Button';
import Checkbox from '../components/UI/Checkbox';
import ErrorMessages from '../components/UI/ErrorMessages';
import Input from '../components/UI/Input';
import Loader from '../components/UI/Loader';
import Modal from '../components/UI/Modal';
import Client from '../models/serviceModels/Client';
import Program from '../models/serviceModels/Program';
import { getClientById } from '../services/clientsService';
import { enrollInProgram, getAvailablePrograms, getEnrolledPrograms, getProgramHistory, unenrollInProgram } from '../services/programsService';
import { formatJSONDateWithoutTime, parseJSONDateWithoutTime } from '../utils/dateHelpers';
import { MeContext } from '../App';
import ValidatedInput from '../components/UI/ValidatedInput';
import ProgramHistory from '../models/serviceModels/ProgramHistory';
import { householdProgramEnrollmentEnabled } from '../utils/features';
import './ParticipantPrograms.css';

const ParticipantPrograms = () => {
    const { id = '' } = useParams();
    const [, participantRefreshVersion = 0] = useOutletContext<[Client | undefined, number]>();
    const [loadingPrograms, setLoadingPrograms] = useState(false);
    const [availablePrograms, setAvailablePrograms] = useState<Program[]>([]);
    const [enrolledPrograms, setEntrolledPrograms] = useState<Program[]>([]);
    const [failedToLoad, setFailedToLoad] = useState(false);
    const [unenrollDateMessages, setUnenrollDateMessages] = useState<string[]>([]);
    const [enrollDateMessages, setEnrollDateMessages] = useState<string[]>([]);
    const [enrolling, setEnrolling] = useState(false);
    const [unenrolling, setUnenrolling] = useState(false);
    const [error, setError] = useState('');
    const [unenrollProgram, setUnenrollProgram] = useState<Program>();
    const [unenrollReason, setUnenrollReason] = useState('');
    const [unenrollDate, setUnenrollDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [enrollProgram, setEnrollProgram] = useState<Program>();
    const [programHistory, setProgramHistory] = useState<ProgramHistory[]>();
    const [enrollDate, setEnrollDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [client, setClient] = useState<Client>();
    const [householdAvailableProgramsByClientId, setHouseholdAvailableProgramsByClientId] = useState<{ [clientId: string]: Program[] }>({});
    const [householdEnrollmentSelections, setHouseholdEnrollmentSelections] = useState<{ [clientId: string]: boolean }>({});
    const me = useContext(MeContext);

    useEffect(() => {
        let cancelled = false;
        setLoadingPrograms(true);
        setFailedToLoad(false);

        const loadPrograms = async () => {
            const clientResult = await getClientById({ id });
            const linkedClientIds = householdProgramEnrollmentEnabled ? [
                ...(clientResult.householdMembers || [])
                    .map(member => member.linkedClientId)
                    .filter(linkedClientId => !!linkedClientId && linkedClientId !== id),
                ...(clientResult.linkedHouseholds || []).flatMap(household => {
                    return [
                        household.owner.clientId,
                        ...household.members.map(member => member.linkedClientId)
                    ];
                }).filter(linkedClientId => !!linkedClientId && linkedClientId !== id)
            ]
                .filter((linkedClientId): linkedClientId is string => !!linkedClientId)
                .filter((linkedClientId, index, linkedClientIds) => linkedClientIds.indexOf(linkedClientId) === index)
                : [];

            const [
                availableProgramsResult,
                enrolledProgramsResult,
                programHistoryResult,
                householdAvailableProgramResults
            ] = await Promise.all([
                getAvailablePrograms(id),
                getEnrolledPrograms(id),
                getProgramHistory(id),
                Promise.all(linkedClientIds.map(linkedClientId => getAvailablePrograms(linkedClientId)))
            ]);

            if (cancelled) {
                return;
            }

            setClient(clientResult);
            availableProgramsResult.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
            setAvailablePrograms(availableProgramsResult);

            enrolledProgramsResult.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
            setEntrolledPrograms(enrolledProgramsResult);

            setProgramHistory(programHistoryResult);

            setHouseholdAvailableProgramsByClientId(linkedClientIds.reduce((previousValue: { [clientId: string]: Program[] }, linkedClientId, index) => {
                previousValue[linkedClientId] = householdAvailableProgramResults[index] || [];
                return previousValue;
            }, {}));
        };

        loadPrograms().catch(e => {
            console.error(e);
            setFailedToLoad(true);
        }).finally(() => {
            if (!cancelled) {
                setLoadingPrograms(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [id, participantRefreshVersion]);

    const getEnrollDate = (program: Program) => {
        setEnrollDate(format(new Date(), 'yyyy-MM-dd'));
        setHouseholdEnrollmentSelections({});
        setEnrollProgram(program);
    }

    const householdMemberName = (firstName?: string, middleName?: string, lastName?: string, fallback = 'Linked Household Member') => {
        return [firstName, middleName, lastName].filter(v => !!v).join(' ').trim() || fallback;
    };

    const getEnrollmentParticipantName = (clientId?: string) => {
        if (!client || !clientId) {
            return 'Participant';
        }

        if (clientId === id) {
            return householdMemberName(client.firstName, client.middleName, client.lastName, 'Participant');
        }

        const ownHouseholdMember = (client.householdMembers || [])
            .find(member => member.linkedClientId === clientId);
        if (ownHouseholdMember) {
            return householdMemberName(ownHouseholdMember.firstName, ownHouseholdMember.middleName, ownHouseholdMember.lastName);
        }

        for (const household of client.linkedHouseholds || []) {
            if (household.owner.clientId === clientId) {
                return householdMemberName(household.owner.firstName, household.owner.middleName, household.owner.lastName, 'Head of Household');
            }

            const linkedHouseholdMember = household.members.find(member => member.linkedClientId === clientId);
            if (linkedHouseholdMember) {
                return householdMemberName(linkedHouseholdMember.firstName, linkedHouseholdMember.middleName, linkedHouseholdMember.lastName);
            }
        }

        return 'Participant';
    };

    const getEnrollmentErrorMessage = (e: any) => {
        if (e.response?.status !== 403) {
            return 'Failed to enroll';
        }

        return `${getEnrollmentParticipantName(e.response?.data?.clientId)} is already enrolled in this program`;
    };

    const isProgramAvailableForClient = (clientId: string) => {
        if (!enrollProgram) {
            return false;
        }

        return (householdAvailableProgramsByClientId[clientId] || [])
            .some(program => program.id === enrollProgram.id);
    };

    const getHouseholdEnrollmentGroups = () => {
        if (!householdProgramEnrollmentEnabled || !client || !enrollProgram) {
            return [];
        }

        const ownHouseholdTargets = (client.householdMembers || [])
            .filter(member => !!member.linkedClientId && member.linkedClientId !== id && isProgramAvailableForClient(member.linkedClientId))
            .map(member => {
                return {
                    clientId: member.linkedClientId!,
                    name: householdMemberName(member.firstName, member.middleName, member.lastName),
                    relationshipName: member.relationshipName
                };
            });

        const groups = [];
        if (ownHouseholdTargets.length > 0) {
            groups.push({
                key: 'own-household',
                title: `${householdMemberName(client.firstName, client.middleName, client.lastName, 'Current participant')} Household`,
                targets: ownHouseholdTargets
            });
        }

        for (const household of client.linkedHouseholds || []) {
            const targets = [];

            if (household.owner.clientId !== id && isProgramAvailableForClient(household.owner.clientId)) {
                targets.push({
                    clientId: household.owner.clientId,
                    name: householdMemberName(household.owner.firstName, household.owner.middleName, household.owner.lastName, 'Head of Household'),
                    relationshipName: 'Head of Household'
                });
            }

            targets.push(...household.members
                .filter(member => !!member.linkedClientId && member.linkedClientId !== id && isProgramAvailableForClient(member.linkedClientId))
                .map(member => {
                    return {
                        clientId: member.linkedClientId!,
                        name: householdMemberName(member.firstName, member.middleName, member.lastName),
                        relationshipName: member.relationshipName
                    };
                }));

            if (targets.length > 0) {
                groups.push({
                    key: `linked-household-${household.owner.clientId}`,
                    title: `${householdMemberName(household.owner.firstName, household.owner.middleName, household.owner.lastName, 'Linked')} Household`,
                    targets
                });
            }
        }

        return groups;
    };

    const getHouseholdEnrollmentTargets = () => {
        return getHouseholdEnrollmentGroups().flatMap(group => group.targets)
            .filter((target, index, targets) => {
                return targets.findIndex(t => t.clientId === target.clientId) === index;
            })
    };

    const handleHouseholdEnrollmentSelectionChanged = (clientId: string, checked: boolean) => {
        setHouseholdEnrollmentSelections({
            ...householdEnrollmentSelections,
            [clientId]: checked
        });
    };

    const triggerEnroll = () => {
        if (!enrollProgram) {
            return;
        }

        setError('');

        if (!enrollDate) {
            setEnrollDateMessages(['Enrollment Date is required']);
            return;
        }

        setEnrolling(true);

        setEntrolledPrograms(p => [
            ...p,
            enrollProgram
        ]);
        setAvailablePrograms(ps => [
            ...ps.filter(p => p.id !== enrollProgram.id)
        ]);

        const additionalClientIds = householdProgramEnrollmentEnabled ? getHouseholdEnrollmentTargets()
            .filter(target => householdEnrollmentSelections[target.clientId])
            .map(target => target.clientId)
            : [];

        enrollInProgram(enrollProgram.id, id, enrollDate, additionalClientIds).then(pr => {
            setEntrolledPrograms(ep => {

                const existingProgram = ep.find(p => p.id === pr.id);
                if (existingProgram) {
                    existingProgram.start = pr.start;
                    existingProgram.updated = pr.updated;
                    existingProgram.updatedBy = pr.updatedBy;
                    existingProgram.stop = undefined;
                    existingProgram.stopReason = undefined;
                }

                return [
                    ...ep
                ];
            });

            setEnrollDate(format(new Date(), 'yyyy-MM-dd'));
        }).catch(e => {
            console.error(e);
            setError(getEnrollmentErrorMessage(e));

            setEntrolledPrograms(p => [
                ...p.filter(p => p.id !== enrollProgram.id)
            ]);
            setAvailablePrograms(ps => [
                ...ps,
                enrollProgram
            ]);
        }).finally(() => {
            setEnrolling(false);
            setEnrollProgram(undefined);
            setHouseholdEnrollmentSelections({});
        });
    };

    const cancelEnrollDate = () => {
        setEnrollProgram(undefined);
        setEnrollDateMessages([]);
        setEnrollDate(format(new Date(), 'yyyy-MM-dd'));
        setHouseholdEnrollmentSelections({});
    }

    const getUnenrollReason = (program: Program) => {
        setUnenrollReason('');
        setUnenrollProgram(program);
    }

    const triggerUnenroll = () => {
        if (!unenrollProgram) {
            return;
        }

        setUnenrollDateMessages([]);
        setError('');

        if (!unenrollDate) {
            setUnenrollDateMessages(['Unenrollment date is required']);
            return;
        }

        if (unenrollProgram.start && new Date(unenrollDate) < new Date(unenrollProgram.start)) {
            setUnenrollDateMessages([`Unrollment date must be equal or before the enrollment date ${format(parseJSONDateWithoutTime(unenrollProgram.start), 'MM/dd/yyyy')}`]);
            return;
        }

        setUnenrolling(true);

        setAvailablePrograms(p => [
            ...p,
            unenrollProgram
        ]);

        setEntrolledPrograms(ps => [
            ...ps.filter(p => p.id !== unenrollProgram.id)
        ]);

        unenrollInProgram(unenrollProgram.id, id, unenrollDate, unenrollReason).then(pr => {
            setAvailablePrograms(ap => {
                const existingProgram = ap.find(p => p.id === pr.id);
                if (existingProgram) {
                    existingProgram.start = pr.start;
                    existingProgram.updated = pr.updated;
                    existingProgram.updatedBy = pr.updatedBy;
                    existingProgram.stop = pr.stop;
                    existingProgram.stopReason = pr.stopReason;
                }

                return [
                    ...ap
                ];
            });

            setProgramHistory([
                {
                    programEnrollmentId: '',
                    programId: pr.id,
                    programName: pr.name,
                    programStart: pr.start || '',
                    programStop: pr.stop || '',
                    programStopReason: pr.stopReason
                },
                ...(programHistory || [])
            ])
            setUnenrollReason('');
        }).catch(e => {
            console.error(e);
            setError('Failed to unenroll');

            setAvailablePrograms(p => [
                ...p.filter(p => p.id !== unenrollProgram.id)
            ]);
            setEntrolledPrograms(ps => [
                ...ps,
                unenrollProgram
            ]);
        }).finally(() => {
            setUnenrolling(false);
            setUnenrollProgram(undefined);
        })
    };

    const cancelUnenrollReason = () => {
        setUnenrollProgram(undefined);
        setUnenrollReason('');
        setUnenrollDateMessages([]);
        setUnenrollDate(format(new Date(), 'yyyy-MM-dd'));
    };

    const getMainContent = () => {
        if (loadingPrograms) {
            return <Loader />;
        }

        if (failedToLoad) {
            return <>Failed to load</>
        }

        return (<>
            <ErrorMessages messages={[error]} />
            <div className="row align-items-center">
                <div className="column-auto">
                    <h3>Active Programs</h3>
                </div>
                <div className="column-auto">
                    {(enrolling || unenrolling) && <Loader />}
                </div>
            </div>
            {(!enrolledPrograms || enrolledPrograms.length === 0) && <div>No active programs</div>}
            {enrolledPrograms && enrolledPrograms.length > 0 && (
                <div className="list-item-container">
                    {enrolledPrograms.map(ep => (
                        <div key={ep.id} className="list-item">
                            <div className="row justify-content-between align-items-center">
                                <div className="column-medium-6">
                                    <div className="row">
                                        <div className="column">
                                            {ep.name}
                                        </div>
                                        <div className="column">
                                            {ep.start && <>Enrolled - {formatJSONDateWithoutTime(ep.start, 'MM/dd/yyyy')}</>}
                                        </div>
                                    </div>
                                </div>
                                <div className="column-medium-auto">
                                    <Protect user={me.user} roles={['Admin', 'Contributor']} behavior="hide"><Button id={`unenroll-${ep.id}`} text="Unenroll"
                                        className="secondary small" onClick={() => getUnenrollReason(ep)} /></Protect>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <h3>Enroll in Programs</h3>
            {(!availablePrograms || availablePrograms.length === 0) && <div>No available programs</div>}
            {availablePrograms && availablePrograms.length > 0 && (
                <div className="list-item-container">
                    {availablePrograms.map(ap => (
                        <div key={ap.id} className="list-item">
                            <div className="row justify-content-between align-items-center">
                                <div className="column-medium-6">
                                    {ap.name}
                                </div>
                                <div className="column-medium-auto">
                                    <Protect user={me.user} roles={['Admin', 'Contributor']} behavior="hide"><Button id={`enroll-${ap.id}`} text="Enroll"
                                        className="outline small" onClick={() => getEnrollDate(ap)} /></Protect>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <h3>Enrollment History</h3>
            {(!programHistory || programHistory.length === 0) && <div>No enrollment history</div>}
            {programHistory && programHistory.length !== 0 && <div className="list-item-container">
                {programHistory.map((ph, i) => {
                    return <div key={i} className="list-item">
                        <div className="row justify-content-between align-items-center">
                            <div className="column-medium-4">
                                {ph.programName}
                            </div>
                            <div className="column-auto">
                                <div className="row">
                                    <div className="column-6">
                                        Enrolled
                                    </div>
                                    <div className="column-6">
                                        {formatJSONDateWithoutTime(ph.programStart, 'MM/dd/yyyy')}
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="column-6">
                                        Unenrolled
                                    </div>
                                    <div className="column-6">
                                        {formatJSONDateWithoutTime(ph.programStop, 'MM/dd/yyyy')}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {ph.programStopReason && <div className="row margin-top-3">
                            <div className="column">
                                <div className="text-bold">Reason for Unenrollment:</div>
                                {ph.programStopReason}
                            </div>
                        </div>}
                    </div>
                })}
            </div>}
        </>);
    };

    return (
        <section id="main-content" tabIndex={-1}>
            {getMainContent()}
            <Modal id="unenroll-program-modal" visible={unenrollProgram !== undefined} className="small" onClose={cancelUnenrollReason} title={`Unenroll from ${unenrollProgram?.name}`}>
                {unenrollProgram && (
                    <>
                        <div className="row">
                            <div className="column">
                                <ValidatedInput id="unenroll-date"
                                    type="date"
                                    label="Unenrollment Date"
                                    value={unenrollDate}
                                    required={true}
                                    messages={unenrollDateMessages}
                                    onChange={setUnenrollDate} />
                            </div>
                        </div>
                        <div className="row margin-top-3">
                            <div className="column">
                                <Input id="unenroll-reason"
                                    type="multi"
                                    label="Provide a Reason (optional):"
                                    value={unenrollReason}
                                    onChange={(e) => setUnenrollReason(e)} />
                            </div>
                        </div>
                        <div className="row margin-top-5">
                            <div className="column">
                                <div className="row justify-content-between">
                                    <div className="column-medium-auto">
                                        <Button id="cancel-activate"
                                            text="Cancel"
                                            className="secondary"
                                            disabled={unenrolling}
                                            onClick={cancelUnenrollReason} />
                                    </div>
                                    <div className="column-medium-auto">
                                        <Button id="confirm-activate"
                                            text="Submit"
                                            loading={unenrolling}
                                            disabled={unenrolling}
                                            onClick={triggerUnenroll} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </Modal>
            <Modal id="enroll-program-modal" visible={enrollProgram !== undefined} className="small" onClose={cancelEnrollDate} title="Select an Enrollment Date">
                {enrollProgram && (
                    <>
                        <div className="row">
                            <div className="column">
                                <label>Enrollment Date:</label>
                                <ValidatedInput id="enroll-date"
                                    type="date"
                                    value={enrollDate}
                                    required={true}
                                    messages={enrollDateMessages}
                                    onChange={(e) => setEnrollDate(e)} />
                            </div>
                        </div>
                        {householdProgramEnrollmentEnabled && <div className="row margin-top-3">
                            <div className="column">
                                <div className="text-bold margin-bottom-2">Participants to Enroll</div>
                                <div className="list-item-container">
                                    <div className="list-item">
                                        <Checkbox id="program-enrollment-current-participant"
                                            label={`${client?.firstName || ''} ${client?.lastName || ''}`.trim() || 'Current Participant'}
                                            checked={true}
                                            disabled={true}
                                            containerClassName="unboxed program-enrollment-target-checkbox" />
                                        <div className="program-enrollment-target-help">Current participant</div>
                                    </div>
                                    {getHouseholdEnrollmentGroups().map((group, groupIndex) => {
                                        return <div key={group.key}>
                                            {groupIndex > 0 && <hr className="program-enrollment-household-divider" />}
                                            <div className="program-enrollment-household-title">{group.title}</div>
                                            {group.targets.map(target => {
                                                return <div className="list-item" key={`${group.key}-${target.clientId}`}>
                                                    <Checkbox id={`program-enrollment-household-${group.key}-${target.clientId}`}
                                                        label={target.name}
                                                        checked={!!householdEnrollmentSelections[target.clientId]}
                                                        onChange={(checked) => handleHouseholdEnrollmentSelectionChanged(target.clientId, checked)}
                                                        containerClassName="unboxed program-enrollment-target-checkbox" />
                                                    <div className="program-enrollment-target-help">
                                                        {target.relationshipName || 'Linked household participant'}
                                                    </div>
                                                </div>
                                            })}
                                        </div>
                                    })}
                                </div>
                                {getHouseholdEnrollmentTargets().length === 0 && <div className="program-enrollment-target-help">
                                    No linked household members have this program available.
                                </div>}
                            </div>
                        </div>}
                        <div className="row margin-top-5">
                            <div className="column">
                                <div className="row justify-content-between">
                                    <div className="column-medium-auto">
                                        <Button id="cancel-activate"
                                            text="Cancel"
                                            className="secondary"
                                            disabled={enrolling}
                                            onClick={cancelEnrollDate} />
                                    </div>
                                    <div className="column-medium-auto">
                                        <Button id="confirm-activate"
                                            text="Submit"
                                            disabled={enrolling}
                                            loading={enrolling}
                                            onClick={triggerEnroll} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </Modal>
        </section>);
};

export default ParticipantPrograms; 
